/**
 * The font a PDF needs before it is written, fetched once per tab and handed to the worker.
 *
 * The PDF writer (src/formats/write/pdf.ts) draws text it can write in WinAnsi with the viewer's
 * own Helvetica, and Chinese, Japanese and Korean with the viewer's standard CJK fonts; neither
 * needs a fetch. Everything else in Latin, Greek or Cyrillic (Polish, Czech, Turkish, Romanian,
 * Russian, Ukrainian, Greek…) needs a font embedded in the file, and until the worker had one
 * the writer refused those documents. `ctx.resources.font` is synchronous, so the face has to be
 * resident before the write starts (src/worker/adapter/fonts.ts): this fetches it on the first
 * write that needs it and sends it with `sendFonts`, and the writer subsets it into the PDF.
 *
 * The face is Liberation Sans (metric-compatible with Arial and Helvetica), from pdf.js's own
 * standard fonts, served from /static/base/fonts with its licence beside it
 * (LICENSE-liberation.txt: GPLv2 with the font exception, so a PDF that embeds it is not
 * covered by the GPL).
 */

import { isWinAnsi, scriptOf } from '../../../../../src/formats/pdf/scripts.ts';
import type { SubtitleWorkerClient } from './worker.ts';

/** The face, beside the site's other fonts. */
export const EXTENDED_FONT_URL = '/static/base/fonts/LiberationSans-Regular.ttf';
/** What the worker files it under, and the scripts it covers (src/worker/adapter/fonts.ts). */
export const EXTENDED_FONT_FAMILY = 'Liberation Sans';
const EXTENDED_SCRIPTS = ['latin', 'greek', 'cyrillic', 'other'] as const;

/** The fetch failed: a network condition the page reports, never a reason to write without it. */
export class PdfFontLoadError extends Error {
  constructor(detail: string) {
    super(`the PDF font did not load: ${detail}`);
    this.name = 'PdfFontLoadError';
  }
}

/**
 * True when `texts` hold a Latin, Greek or Cyrillic character outside WinAnsi: the characters
 * only an embedded font can show.
 */
export function needsExtendedFont(texts: Iterable<string>): boolean {
  for (const text of texts) {
    for (const ch of text) {
      const cp = ch.codePointAt(0) as number;
      if (isWinAnsi(cp)) continue;
      const script = scriptOf(cp);
      if (script === 'latin' || script === 'greek' || script === 'cyrillic') return true;
    }
  }
  return false;
}

/** One fetch per tab, shared by every write that needs it; forgotten again if it fails. */
let pending: Promise<void> | null = null;
/** The client the face was sent to. A respawned worker has lost it. */
let sentTo: SubtitleWorkerClient | null = null;

/**
 * Make sure the worker holds the font `texts` need, if any. Resolves at once when none is
 * needed or it is already there; rejects with `PdfFontLoadError` when the fetch fails.
 */
export function ensurePdfFonts(
  client: SubtitleWorkerClient,
  texts: Iterable<string>,
): Promise<void> {
  if (!needsExtendedFont(texts)) return Promise.resolve();
  if (pending !== null && sentTo === client) return pending;
  const build = document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim();
  const url = build ? `${EXTENDED_FONT_URL}?v=${encodeURIComponent(build)}` : EXTENDED_FONT_URL;
  sentTo = client;
  pending = fetch(url)
    .then((response) => {
      if (!response.ok) throw new PdfFontLoadError(`HTTP ${response.status}`);
      return response.arrayBuffer();
    })
    .then(async (buffer) => {
      // `sendFonts` drops the bytes when no worker is running yet.
      await client.start();
      client.sendFonts([
        {
          family: EXTENDED_FONT_FAMILY,
          scripts: [...EXTENDED_SCRIPTS],
          bytes: new Uint8Array(buffer),
        },
      ]);
    })
    .catch((err: unknown) => {
      pending = null;
      sentTo = null;
      throw err instanceof PdfFontLoadError ? err : new PdfFontLoadError(String(err));
    });
  return pending;
}

/** The worker was replaced and holds no fonts: the next write that needs one fetches it again. */
export function forgetPdfFonts(): void {
  pending = null;
  sentTo = null;
}
